import { PlansPopup } from "@components/molecules/PlansPopup";
import { EmergencyTemplate } from "@components/templates/emergency";
import { useGo, useTranslation } from "@refinedev/core";
import { CanAccess } from "@refinedev/core";
import { HTMLAttributes, useState } from "react";
import { FaPhone, FaUsers, FaTriangleExclamation, FaPlay, FaXmark } from "react-icons/fa6";

interface Option extends HTMLAttributes<HTMLDivElement> {
    title: string,
    icon: React.ReactNode,
    description: string,
    color: string
}

export const Helps = () => {
    const go = useGo();
    const { translate } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const data: Option[] = [
        {
            title: translate("page.helps.options.contactProfessional.title"),
            description: translate("page.helps.options.contactProfessional.description"),
            icon: <FaPhone className="w-6 h-6" />,
            color: 'bg-primary',
            onClick: () => { go({ to: "/contact-professional" }) }
        },
        {
            title: translate("page.helps.options.contactSupportNetwork.title"),
            description: translate("page.helps.options.contactSupportNetwork.description"),
            icon: <FaUsers className="w-6 h-6" />,
            color: 'bg-secondary',
            onClick: () => { /* Add functionality */ }
        },
        {
            title: translate("page.helps.options.emergencyHotline.title"),
            description: translate("page.helps.options.emergencyHotline.description"),
            icon: <FaTriangleExclamation className="w-6 h-6" />,
            color: 'bg-error',
            onClick: () => { /* Add emergency call functionality */ }
        },
    ]

    return <EmergencyTemplate>
        <CanAccess resource="helps" action="show" params={{ validateSubscription: true }}
            fallback={<PlansPopup />}>
            <div className="flex flex-col items-center xl:justify-center xl:h-full p-4">
                <div className="w-full max-w-md space-y-4">
                    <div className="text-center mb-8">
                        <h1 className="text-2xl font-bold text-base-content mb-2">{translate("page.helps.title")}</h1>
                        <p className="text-base-content/70">{translate("page.helps.subtitle")}</p>
                    </div>

                    {data.map((option, i) =>
                        <Card key={i} {...option} />
                    )}

                    <button
                        className="btn btn-warning w-full gap-2"
                        onClick={() => setIsModalOpen(true)}
                    >
                        <FaPlay className="w-4 h-4" />
                        {translate("page.helps.firstAidGuide")}
                    </button>
                </div>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                    <div className="bg-base-100 rounded-lg shadow-xl max-w-2xl w-full">
                        <div className="flex justify-between items-center p-4 border-b border-base-300">
                            <h3 className="text-lg font-semibold">{translate("page.helps.modal.title")}</h3>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="btn btn-ghost btn-sm btn-circle"
                            >
                                <FaXmark className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="p-4">
                            <iframe
                                className="w-full h-64 md:h-96 rounded"
                                src="https://www.youtube.com/embed/iuqo8NxQcHU?si=8osFEh69ECJ8zQU_"
                                title={translate("page.helps.modal.videoTitle")}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                referrerPolicy="strict-origin-when-cross-origin"
                                allowFullScreen
                            />
                        </div>
                    </div>
                </div>
            )}
        </CanAccess>
    </EmergencyTemplate>;
}

const Card = ({ icon, title, description, color, ...rest }: Option) =>
    <div className={`card bg-base-100 shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer border border-base-300 ${rest.onClick ? 'hover:scale-[1.02]' : ''}`} {...rest}>
        <div className="card-body p-4">
            <div className="flex items-center gap-4">
                <div className={`${color} text-white p-3 rounded-full flex-shrink-0`}>
                    {icon}
                </div>
                <div className="flex-1">
                    <h2 className="card-title text-lg mb-2">{title}</h2>
                    <p className="text-base-content/70 text-sm leading-relaxed">{description}</p>
                </div>
            </div>
        </div>
    </div>
